import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import * as Styled from './LoyaltyPageV3.styles';
import { selectors } from '../LoyaltyPage/reducer';
import { selectors as selectorsLogin } from '../LoginPage/reducer';
import * as actions from '../LoyaltyPage/actions';
import {
  deleteFromLocalStorage,
  setToLocalStorage,
} from '../../global/helpers';
import { WarningBlock } from '../LoyaltyPage/components/WarningBlock';
import LoyaltyClientScreen from '../LoyaltyPageSeparated/components/LoyaltyClientScreen';
import LoyaltyReceiptScreen from '../LoyaltyPageSeparated/components/LoyaltyReceiptScreen';
import LoyaltyApplyScreen from '../LoyaltyPageSeparated/components/LoyaltyApplyScreen';
import type {
  ConfirmedLoyaltyAmounts,
  StandartReceiptItem,
} from '../LoyaltyPageSeparated/components/LoyaltyApplyScreen';
import type { PaymentMethod } from '../LoyaltyPageSeparated/components/LoyaltyPaymentScreen';
import type { GetClientOrdersData } from '../LoyaltyPage/types';
import LoyaltyProcessingScreen from '../LoyaltyPageSeparated/components/LoyaltyProcessingScreen';
import { ScreenType } from '../LoyaltyPageSeparated/components/LoyaltyTabs';
import LoyaltyPaymentScreenV3 from './components/LoyaltyPaymentScreenV3';
import { loyaltyHelpTagEnabled } from './components/loyaltyHelpGate';
import type {
  BonusHelpState,
  CouponHelpState,
  PaymentHelpState,
} from './components/loyaltyHelpContent';
import LoyaltyCouponBonusScreen from './coupons/LoyaltyCouponBonusScreen';
import {
  couponAvailabilityError,
  couponReceiptError,
  couponsTagEnabled,
  quoteError,
} from './coupons/model';
import { CouponError, CouponService, couponErrorKey } from './coupons/service';
import {
  clearCouponRecovery,
  CouponRecovery,
  readCouponRecovery,
  saveCouponRecovery,
} from './coupons/recovery';
import { watchCouponReceipt } from './coupons/monitor';
import * as CouponStyled from './coupons/Coupon.styles';
import * as V3 from '../LoyaltyPageSeparated/LoyaltyPageSeparated.styles';

type V3Step = 'client' | 'receipt' | 'bonus' | 'payment';

const LoyaltyHelpBubble = lazy(() => import('./components/LoyaltyHelpBubble'));

const stepForScreen = (screen: ScreenType): V3Step => {
  if (screen === 'apply') return 'bonus';
  if (screen === 'payment' || screen === 'processing') return 'payment';
  return screen;
};

const steps: { id: V3Step; label: string; number: number }[] = [
  { id: 'client', label: 'Клиент', number: 1 },
  { id: 'receipt', label: 'Чек', number: 2 },
  { id: 'bonus', label: 'Бонусы', number: 3 },
  { id: 'payment', label: 'Оплата', number: 4 },
];

const LoyaltyPageV3 = ({
  onTrainingScreenChange,
  training = false,
}: {
  onTrainingScreenChange?: (screen: ScreenType) => void;
  training?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dataUser = useSelector(selectorsLogin.userData);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const clientInfo = useSelector(selectors.clientInfo);
  const submitOrderResult = useSelector(selectors.submitOrderResult);
  const submitOrderLoading = useSelector(selectors.submitOrderLoading);
  const helpTransferError = useSelector(selectors.sendToStandartError);
  const helpTransferSuccess = useSelector(selectors.sendToStandartSuccess);
  const helpPayments = useSelector(selectors.partnerPayments);
  const helpClientError = useSelector(selectors.clientInfoError);
  const helpReceiptError = useSelector(selectors.currentReceiptError);
  const helpReceipts = useSelector(selectors.currentReceipt);
  const helpOrderError = useSelector(selectors.submitOrderError);
  const helpHealthError = useSelector(selectors.healthStatusError);
  const helpStada = useSelector(selectors.stadaInfo);
  const [couponHelpState, setCouponHelpState] =
    useState<CouponHelpState | null>(null);
  const [couponEntryError, setCouponEntryError] = useState('');
  const [bonusHelpState, setBonusHelpState] = useState<BonusHelpState | null>(
    null
  );
  const [paymentHelpState, setPaymentHelpState] =
    useState<PaymentHelpState | null>(null);
  const isDev = process.env.REACT_APP_DEV_AREA === 'true';
  const couponsEnabled = couponsTagEnabled(isDev, dataUser?.sourceTags);
  const helpEnabled = loyaltyHelpTagEnabled(
    isDev,
    dataUser?.networkTags,
    dataUser?.sourceTags
  );
  const [entryCouponCode, setEntryCouponCode] = useState<string | null>(null);
  const [couponLocked, setCouponLocked] = useState(false);
  const [couponOrderConfirmed, setCouponOrderConfirmed] = useState(false);
  const [couponRecovery, setCouponRecovery] = useState<CouponRecovery | null>(
    null
  );
  const [recoveryMonitorError, setRecoveryMonitorError] = useState(false);

  useEffect(() => {
    setCouponRecovery(
      couponsEnabled ? readCouponRecovery(dataUser?.sourceCode) : null
    );
  }, [couponsEnabled, dataUser?.sourceCode]);

  const [currentScreen, setCurrentScreen] = useState<ScreenType>('client');
  useEffect(() => {
    onTrainingScreenChange?.(currentScreen);
  }, [currentScreen, onTrainingScreenChange]);
  const [confirmedStandartItems, setConfirmedStandartItems] = useState<
    StandartReceiptItem[]
  >([]);
  const [confirmedAmounts, setConfirmedAmounts] =
    useState<ConfirmedLoyaltyAmounts | null>(null);
  // V3 separates receipt verification from the bonus decision.
  // The receipt must therefore stay undiscounted until the pharmacist reaches
  // the dedicated bonus step and confirms the customer's one-time code.
  const [showWithBonuses, setShowWithBonuses] = useState(false);
  const [hasAutoTransitioned, setHasAutoTransitioned] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<GetClientOrdersData | null>(
    null
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    null
  );

  const partnerPercent = useMemo(
    () => (dataUser?.networkCode === 'ot_a_do_ya' ? 5 : 1),
    [dataUser]
  );

  const isStadaBonusesCredit = useMemo(
    () =>
      dataUser?.networkTags?.some((tag: { tag_id: number }) =>
        isDev ? tag.tag_id === 1047 : tag.tag_id === 1190
      ) ?? false,
    [dataUser, isDev]
  );

  const isPartnersPayment = useMemo(
    () =>
      dataUser?.sourceTags?.some((tag: { tag_id: number }) =>
        isDev ? tag.tag_id === 1048 : tag.tag_id === 1192
      ) ?? false,
    [dataUser, isDev]
  );

  const resetAllState = useCallback(() => {
    setConfirmedAmounts(null);
    setShowWithBonuses(false);
    setCurrentScreen('client');
    setHasAutoTransitioned(false);
    setOrderConfirmed(false);
    setPendingOrder(null);
    setPaymentMethod(null);
    setEntryCouponCode(null);
    setCouponLocked(false);
    setCouponOrderConfirmed(false);
    clearCouponRecovery(dataUser?.sourceCode);
    setCouponRecovery(null);
    setRecoveryMonitorError(false);
    if (!training) deleteFromLocalStorage('chosenReceipt');
    dispatch(actions.setChosenReceipt.request(null));
    dispatch(actions.resetClientInfo());
    dispatch(actions.resetCurrentReceipt());
    dispatch(actions.resetApplyBonuses());
    dispatch(actions.resetSubmitOrder());
  }, [dispatch, dataUser?.sourceCode, training]);

  const handleCouponEnded = useCallback(
    (completion?: { docId: string; orderNumber?: string }) => {
      if (completion && !completion.orderNumber && dataUser?.sourceCode) {
        // Do not erase evidence of a redeemed/reserved coupon without an order.
        // This is a finished cashier workflow requiring order reconciliation,
        // not a fresh coupon entry and not a proof of successful payment.
        const marker: CouponRecovery = {
          sourceCode: dataUser.sourceCode,
          docId: completion.docId,
          phase: 'reconcile',
        };
        try {
          saveCouponRecovery(marker);
        } catch {
          setRecoveryMonitorError(true);
        }
        setCouponRecovery(marker);
        return;
      }
      resetAllState();
    },
    [dataUser?.sourceCode, resetAllState]
  );

  useEffect(() => {
    // A known reservation may have ended while the page was closed. Resume
    // read-only monitoring, never confirmation, for that exact saved receipt.
    if (couponRecovery?.phase !== 'confirmed') return undefined;
    let number = couponRecovery.orderNumber;
    const watcher = watchCouponReceipt(
      CouponService,
      couponRecovery.docId,
      {
        onProgress: (progress) => {
          setRecoveryMonitorError(progress.syncError);
          if (!progress.orderNumber || progress.orderNumber === number) return;
          number = progress.orderNumber;
          const marker = { ...couponRecovery, orderNumber: number };
          try {
            saveCouponRecovery(marker);
            setCouponRecovery(marker);
          } catch {
            setRecoveryMonitorError(true);
          }
        },
        onEnded: () =>
          handleCouponEnded({
            docId: couponRecovery.docId,
            orderNumber: number,
          }),
        onError: () => setRecoveryMonitorError(true),
      },
      true
    );
    return watcher.stop;
  }, [couponRecovery, handleCouponEnded]);

  useEffect(() => {
    if (dataUser?.sourceCode) {
      dispatch(
        actions.getHealthStatus.request({ source_code: dataUser.sourceCode })
      );
    }
  }, [dataUser, dispatch]);

  useEffect(() => {
    if (!clientInfo && !entryCouponCode && !couponLocked && !couponRecovery) {
      if (!training) deleteFromLocalStorage('chosenReceipt');
      dispatch(actions.setChosenReceipt.request(null));
      dispatch(actions.resetApplyBonuses());
      dispatch(actions.resetSubmitOrder());
      setShowWithBonuses(false);
      setHasAutoTransitioned(false);
      setOrderConfirmed(false);
      setPaymentMethod(null);
    }
  }, [
    clientInfo,
    entryCouponCode,
    couponLocked,
    couponRecovery,
    dispatch,
    training,
  ]);

  const needsPolling =
    currentScreen !== 'client' &&
    !couponLocked &&
    !couponOrderConfirmed &&
    !couponRecovery;
  const getReceiptInfo = useCallback(() => {
    dispatch(actions.getCurrentReceipt.request());
  }, [dispatch]);

  useEffect(() => {
    if (dataUser && needsPolling) getReceiptInfo();
  }, [dataUser, needsPolling, getReceiptInfo]);

  useEffect(() => {
    if (!dataUser || !needsPolling) return undefined;
    const intervalId = window.setInterval(getReceiptInfo, 5000);
    return () => window.clearInterval(intervalId);
  }, [dataUser, needsPolling, getReceiptInfo]);

  useEffect(() => {
    // The coupon screen monitors its fixed doc_id after reservation, including
    // a failed final price transfer. Do not reset it from stale Redux data.
    if (
      !couponOrderConfirmed &&
      !couponLocked &&
      !couponRecovery &&
      !chosenReceipt?.items?.length &&
      submitOrderResult
    )
      resetAllState();
  }, [
    chosenReceipt,
    submitOrderResult,
    couponOrderConfirmed,
    couponLocked,
    couponRecovery,
    resetAllState,
  ]);

  const handleClientAdded = useCallback(() => {
    if (!hasAutoTransitioned) {
      setHasAutoTransitioned(true);
      setCurrentScreen('receipt');
    }
  }, [hasAutoTransitioned]);

  const handlePendingOrderRestored = useCallback(() => {
    setConfirmedAmounts(null);
    setPendingOrder(null);
    setOrderConfirmed(true);
    setCurrentScreen('payment');
  }, []);

  const handleOrderConfirmed = useCallback(
    (
      items?: StandartReceiptItem[],
      couponCheckout?: boolean,
      amounts?: ConfirmedLoyaltyAmounts
    ) => {
      if (!isPartnersPayment && !couponCheckout) return;
      setConfirmedStandartItems(items ?? []);
      setConfirmedAmounts(amounts ?? null);
      setOrderConfirmed(true);
      // Coupon-only checkout has just one payment method: send it to the
      // cashier immediately, without a redundant payment-choice step.
      if (couponCheckout && !clientInfo?.phone) {
        setPaymentMethod('cashier');
        setCurrentScreen('processing');
      } else {
        setCurrentScreen('payment');
      }
    },
    [isPartnersPayment, clientInfo?.phone]
  );

  const handlePaymentMethodSelected = useCallback((method: PaymentMethod) => {
    setPaymentMethod(method);
    setCurrentScreen('processing');
  }, []);

  const handleCouponEntered = useCallback(
    async (code: string, docId?: string) => {
      if (!docId) {
        try {
          const entryQuote = await CouponService.check(code);
          if (entryQuote?.status !== 'success')
            throw new CouponError('INVALID_RESPONSE');
          const entryError = couponAvailabilityError(entryQuote.availability);
          if (entryError) throw new CouponError(entryError);
        } catch (error) {
          // Both HTTP 400 and an error body in HTTP 200 can require receipt
          // selection. Resolve that state through the shared error mapper;
          // coupon availability is checked again for the selected receipt.
          if (couponErrorKey(error) !== 'SELECT_RECEIPT') throw error;
        }
        deleteFromLocalStorage('chosenReceipt');
        setEntryCouponCode(code);
        dispatch(actions.resetClientInfo());
        dispatch(actions.resetSubmitOrder());
        dispatch(actions.setChosenReceipt.request(null));
        setCurrentScreen('receipt');
        return;
      }
      const quote = await CouponService.check(code, docId);
      if (quote?.status !== 'success')
        throw new CouponError('INVALID_RESPONSE');
      const availabilityError = couponAvailabilityError(quote.availability);
      if (availabilityError) throw new CouponError(availabilityError);
      const quoteDocId = String(quote.doc_id ?? '').trim();
      if (!quoteDocId) throw new CouponError('INVALID_RESPONSE');
      if (quoteDocId !== String(docId))
        throw new CouponError('RECEIPT_CHANGED');
      const receipt = await CouponService.receipt(quoteDocId);
      const receiptError = quoteError(receipt, quote);
      if (receiptError) throw new CouponError(receiptError);
      setToLocalStorage('chosenReceipt', receipt);
      setEntryCouponCode(code);
      dispatch(actions.resetClientInfo());
      dispatch(actions.resetSubmitOrder());
      dispatch(actions.setChosenReceipt.request(receipt));
      setCurrentScreen('receipt');
    },
    [dispatch]
  );

  const isStepLocked = (step: V3Step) =>
    couponLocked ||
    !!couponRecovery ||
    submitOrderLoading ||
    (step !== 'payment' && (orderConfirmed || !!submitOrderResult));

  const goToStep = (step: V3Step) => {
    if (isStepLocked(step)) return;
    if (step === 'client') {
      setEntryCouponCode(null);
      setCurrentScreen('client');
    }
    if (step === 'receipt' && (clientInfo || entryCouponCode))
      setCurrentScreen('receipt');
    if (step === 'bonus' && chosenReceipt && (clientInfo || entryCouponCode))
      setCurrentScreen('apply');
    if (step === 'payment' && orderConfirmed) setCurrentScreen('payment');
  };

  const activeStep =
    couponOrderConfirmed || couponRecovery
      ? 'payment'
      : stepForScreen(currentScreen);
  const activeStepIndex = steps.findIndex((step) => step.id === activeStep);

  const renderScreen = () => {
    if (couponRecovery && !orderConfirmed) {
      return (
        <V3.V3BonusScreen>
          <V3.V3BonusContent>
            <V3.V3BonusTitle>
              {t(
                couponRecovery.phase === 'reconcile'
                  ? 'LoyaltyCoupons.RECONCILE_TITLE'
                  : 'LoyaltyCoupons.RECOVERY_TITLE'
              )}
            </V3.V3BonusTitle>
            <CouponStyled.Error role="alert">
              {t(
                couponRecovery.phase === 'reconcile'
                  ? 'LoyaltyCoupons.RECONCILE_HINT'
                  : 'LoyaltyCoupons.RECOVERY_HINT',
                {
                  docId: couponRecovery.docId,
                }
              )}
            </CouponStyled.Error>
            {recoveryMonitorError && (
              <CouponStyled.Error role="status">
                {t('LoyaltyCoupons.Errors.RECEIPT_MONITOR_ERROR')}
              </CouponStyled.Error>
            )}
            {couponRecovery.orderNumber && (
              <V3.V3ReceiptCaption>
                {t('LoyaltyV3.ORDER_NUMBER')} {couponRecovery.orderNumber}
              </V3.V3ReceiptCaption>
            )}
          </V3.V3BonusContent>
          <V3.V3BonusFooter>
            <V3.V3SecondaryButton
              type="button"
              onClick={() => {
                // Acknowledgement only; it never cancels an order or releases a coupon.
                // eslint-disable-next-line no-alert
                if (window.confirm(t('LoyaltyCoupons.RECOVERY_ACK')))
                  resetAllState();
              }}
            >
              {t('LoyaltyCoupons.RECOVERY_CONTINUE')}
            </V3.V3SecondaryButton>
          </V3.V3BonusFooter>
        </V3.V3BonusScreen>
      );
    }
    switch (currentScreen) {
      case 'client':
        return (
          <LoyaltyClientScreen
            onClientAdded={handleClientAdded}
            isStadaBonusesCredit={isStadaBonusesCredit}
            isPartnersPayment={isPartnersPayment}
            onScreenChange={setCurrentScreen}
            onCouponEntered={couponsEnabled ? handleCouponEntered : undefined}
            onPendingOrderState={setPendingOrder}
            onPendingOrderRestored={handlePendingOrderRestored}
            onCouponErrorChange={helpEnabled ? setCouponEntryError : undefined}
          />
        );
      case 'receipt':
        return (
          <LoyaltyReceiptScreen
            training={training}
            showWithBonuses={showWithBonuses}
            setShowWithBonuses={setShowWithBonuses}
            allowBonusPreview={false}
            variant="v3"
            onProceedToConfirm={() => setCurrentScreen('apply')}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            pendingOrder={pendingOrder}
          />
        );
      case 'apply':
        if (couponsEnabled) {
          return (
            <LoyaltyCouponBonusScreen
              entryCode={entryCouponCode}
              partnerPercent={partnerPercent}
              isStadaBonusesCredit={isStadaBonusesCredit}
              isPartnersPayment={isPartnersPayment}
              setShowWithBonuses={setShowWithBonuses}
              onOrderConfirmed={handleOrderConfirmed}
              onBack={() => setCurrentScreen('receipt')}
              onLockedChange={setCouponLocked}
              onHelpStateChange={helpEnabled ? setCouponHelpState : undefined}
              onBonusHelpStateChange={
                helpEnabled ? setBonusHelpState : undefined
              }
              onCouponConfirmed={(recovery) => {
                setCouponOrderConfirmed(true);
                setCouponRecovery(recovery);
              }}
              onRestart={handleCouponEnded}
            />
          );
        }
        return (
          <LoyaltyApplyScreen
            setShowWithBonuses={setShowWithBonuses}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            onOrderConfirmed={handleOrderConfirmed}
            isPartnersPayment={isPartnersPayment}
            variant="v3"
            onBackToReceipt={() => setCurrentScreen('receipt')}
            onHelpStateChange={helpEnabled ? setBonusHelpState : undefined}
          />
        );
      case 'payment':
        return (
          <LoyaltyPaymentScreenV3
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            onBack={() => setCurrentScreen('apply')}
            showBack={false}
            confirmedStandartItems={confirmedStandartItems}
            confirmedAmounts={confirmedAmounts}
            onSelectPaymentMethod={handlePaymentMethodSelected}
            onHelpStateChange={helpEnabled ? setPaymentHelpState : undefined}
          />
        );
      case 'processing':
        return paymentMethod ? (
          <LoyaltyProcessingScreen
            paymentMethod={paymentMethod}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            variant="v3"
            confirmedStandartItems={confirmedStandartItems}
            confirmedAmounts={confirmedAmounts}
            automaticCashierPayment={couponOrderConfirmed && !clientInfo?.phone}
            onBack={() => {
              setPaymentMethod(null);
              setCurrentScreen('payment');
            }}
          />
        ) : (
          <LoyaltyPaymentScreenV3
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            onBack={() => setCurrentScreen('apply')}
            showBack={false}
            confirmedStandartItems={confirmedStandartItems}
            confirmedAmounts={confirmedAmounts}
            onSelectPaymentMethod={handlePaymentMethodSelected}
            onHelpStateChange={helpEnabled ? setPaymentHelpState : undefined}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Styled.Page data-testid="loyalty-v3">
      <WarningBlock />
      <Styled.Boundary>
        <Styled.ModuleHeading>
          <h1>Программа лояльности</h1>
          {helpEnabled && (
            <Suspense fallback={null}>
              <LoyaltyHelpBubble
                step={activeStep}
                coupon={!!entryCouponCode}
                processing={currentScreen === 'processing'}
                appPayment={paymentMethod === 'app'}
                appPaid={
                  !!helpPayments?.some((payment) => payment.status === 'paid')
                }
                creating={submitOrderLoading}
                recovery={
                  !!couponRecovery &&
                  currentScreen !== 'payment' &&
                  currentScreen !== 'processing'
                }
                transferError={!!helpTransferError}
                transferSuccess={!!helpTransferSuccess}
                couponsAvailable={couponsEnabled}
                hasLoyaltyClient={!!clientInfo?.phone}
                couponReceiptEligible={
                  chosenReceipt ? !couponReceiptError(chosenReceipt) : undefined
                }
                cashierConfirmation={!!paymentHelpState?.cashierConfirmation}
                paymentMethodBusy={!!paymentHelpState?.busy}
                reconcile={couponRecovery?.phase === 'reconcile'}
                missingOrder={
                  couponRecovery?.phase === 'confirmed' &&
                  !couponRecovery.orderNumber
                }
                couponState={currentScreen === 'apply' ? couponHelpState : null}
                couponEntryError={
                  currentScreen === 'client' ? couponEntryError : ''
                }
                clientError={!!helpClientError}
                receiptError={!!helpReceiptError}
                receiptsEmpty={
                  Array.isArray(helpReceipts) && helpReceipts.length === 0
                }
                selectedReceipt={!!chosenReceipt}
                useBonuses={bonusHelpState?.useBonuses ?? showWithBonuses}
                noBonuses={
                  bonusHelpState
                    ? !bonusHelpState.available
                    : !!clientInfo &&
                      Number(clientInfo.balance || 0) +
                        (isStadaBonusesCredit
                          ? Number(helpStada?.balance || 0)
                          : 0) <=
                        0
                }
                orderError={!!helpOrderError || !!bonusHelpState?.error}
                orderExists={orderConfirmed}
                submitted={!!submitOrderResult}
                pendingOrder={!!pendingOrder}
                healthError={!!helpHealthError}
                appFailed={
                  !!helpPayments?.some((payment) =>
                    ['failed', 'error'].includes(
                      String(payment.status).toLowerCase()
                    )
                  )
                }
              />
            </Suspense>
          )}
        </Styled.ModuleHeading>

        <Styled.Steps aria-label="Этапы оформления">
          {steps.map((step, index) => {
            const completed = index < activeStepIndex;
            const enabled =
              step.id === 'client' ||
              (step.id === 'receipt' && !!(clientInfo || entryCouponCode)) ||
              (step.id === 'bonus' &&
                !!chosenReceipt &&
                !!(clientInfo || entryCouponCode)) ||
              (step.id === 'payment' && orderConfirmed);
            return (
              <Styled.Step
                key={step.id}
                className={`${activeStep === step.id ? 'active' : ''} ${
                  completed ? 'completed' : ''
                }`}
              >
                <button
                  type="button"
                  disabled={!enabled || isStepLocked(step.id)}
                  onClick={() => goToStep(step.id)}
                >
                  <span className="number">
                    {completed ? '✓' : step.number}
                  </span>
                  <span>{step.label}</span>
                </button>
              </Styled.Step>
            );
          })}
        </Styled.Steps>

        <Styled.WorkCard>
          {entryCouponCode && !clientInfo && currentScreen !== 'client' && (
            <Styled.ClientHeader data-v3-client-header>
              <span className="client-status">
                {t('LoyaltyCoupons.COUPON_ENTRY')}
              </span>
              <span>{t('LoyaltyCoupons.NO_LOYALTY_CLIENT')}</span>
            </Styled.ClientHeader>
          )}
          {clientInfo && currentScreen !== 'client' && (
            <Styled.ClientHeader data-v3-client-header>
              <span className="client-status">
                <span aria-hidden>✓</span> {t('LoyaltyV3.CLIENT_ADDED')}
              </span>
              <span className="balance">
                <strong>
                  {Number(clientInfo.balance || 0).toLocaleString('ru-RU')}
                </strong>
                <span>{t('LoyaltyV3.PHARMACY_BONUSES')}</span>
              </span>
            </Styled.ClientHeader>
          )}
          {renderScreen()}
        </Styled.WorkCard>
        {isDev && (
          <Styled.DevMarker>
            DEV · новый интерфейс включён тегом{' '}
            {couponsEnabled ? 'loyalty_v3.1' : 'loyalty_v3'}
          </Styled.DevMarker>
        )}
      </Styled.Boundary>
    </Styled.Page>
  );
};

export default LoyaltyPageV3;
